import React, { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import HeroFallbackImage from './images/heroFallback.jpg'
import AppScreenImage from './images/appScreen.png'
import ChargerImage from './images/charger.png'
import './NewLandingPage.css'
import Loader from './../navigationComponents/loader/Loader'
import { useCookies } from 'react-cookie'
import { PublicPropertyApi } from '../apis/PublicPropertyApi'
import { GpButton, Icon } from '../components/gp'

const STEPS = [
    {
        icon: 'qr',
        title: 'Set up in minutes',
        body: "Match your unit, add your vehicle, and you're approved by your property.",
    },
    {
        icon: 'car',
        title: 'Tell us your miles',
        body: "Set how far you drive — GoPower plans the charge so your car's ready.",
    },
    {
        icon: 'plug',
        title: 'Plug in & go',
        body: 'Charge at your own port at the lowest available rate, automatically.',
    },
]

const APP_BULLETS = [
    "Set your miles and plug in — that's it",
    'Standard and high-speed charging available',
    'Always billed at the lowest available rate',
]

const VALUE_PROPS = [
    {
        icon: 'plug',
        title: 'A personal charging port',
        body: 'No waiting lists, no tension over a shared charger. Your spot is always ready.',
    },
    {
        icon: 'car',
        title: 'Works with any EV',
        body: 'Compatible with every EV, plug-in hybrid and electric bike.',
    },
    {
        icon: 'clock',
        title: 'Affordable charging',
        body: 'GoPower seeks off-peak windows so you automatically pay the best price.',
    },
]

const NewLandingPage = (props) => {
    const [apartmentsName, setApartmentsName] = useState(undefined)
    const [apartmentsHero, setApartmentsHero] = useState(undefined)
    const [apartmentsLogo, setApartmentsLogo] = useState(undefined)
    const [loading, setLoading] = useState(false)
    const [propertyUuid, setPropertyUuid] = useCookies(['propertyUuid'])
    const history = useHistory()
    let { pathId } = useParams()

    const goToSignUp = () => {
        history.push('/sign-up')
    }

    const goToLogin = () => {
        history.push('/sign-in')
    }

    useEffect(() => {
        const propertyUuid = pathId
        if (propertyUuid) {
            setPropertyUuid('propertyUuid', propertyUuid, { path: '/' })
            setLoading(true)
            PublicPropertyApi.get(propertyUuid)
                .then((r) => {
                    setApartmentsName(r?.name)
                    if (r?.hero?.length > 0) {
                        setApartmentsHero(r?.hero[0]?.url ?? '')
                    }
                    if (r?.logo?.length > 0) {
                        setApartmentsLogo(r?.logo[0]?.url ?? '')
                    }
                })
                .finally(() => setLoading(false))
        }
    }, [pathId])

    const heroImage = apartmentsHero || HeroFallbackImage

    return (
        <>
            {loading && <Loader />}
            {!loading && (
                <div className="new-landing-page-container gp-app">
                    {/* ─── Hero ─── */}
                    <section className="lp-hero">
                        <div className="lp-hero-grid">
                            <div className="lp-hero-copy">
                                <span className="lp-badge">
                                    <Icon name="qr" size={15} />
                                    Scanned at your building
                                </span>
                                <div className="lp-eyebrow">
                                    EV charging at
                                </div>
                                <h1 className="lp-hero-title">
                                    {apartmentsName}
                                </h1>
                                <p className="lp-hero-subhead">
                                    Your building is now GoPower-equipped. Set
                                    up your account and start charging right
                                    where you park.
                                </p>
                                <div className="lp-cta-stack">
                                    <GpButton
                                        variant="primary"
                                        icon="bolt"
                                        className="lp-cta-button"
                                        onClick={goToSignUp}
                                    >
                                        Get started
                                    </GpButton>
                                    <div className="lp-signin-hint">
                                        Already a member?{' '}
                                        <a onClick={goToLogin}>Log in</a>
                                    </div>
                                </div>
                            </div>
                            <div className="lp-hero-image-wrap">
                                <div
                                    className="lp-hero-image"
                                    style={{
                                        backgroundImage: `url('${heroImage}')`,
                                    }}
                                >
                                    <div className="lp-hero-image-overlay" />
                                    <div className="lp-hero-image-caption">
                                        {apartmentsLogo && (
                                            <img
                                                alt={`${apartmentsName} logo`}
                                                className="lp-property-logo"
                                                src={apartmentsLogo}
                                            />
                                        )}
                                        <div className="lp-hero-image-kicker">
                                            NOW CHARGING-READY
                                        </div>
                                        <div className="lp-hero-image-name">
                                            {apartmentsName}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ─── How it works ─── */}
                    <section className="lp-section">
                        <div className="lp-section-inner">
                            <div className="lp-section-head">
                                <div className="lp-eyebrow-label">
                                    How it works
                                </div>
                                <h2 className="lp-h2">
                                    Charging at home, in three steps
                                </h2>
                            </div>
                            <div className="lp-step-grid">
                                {STEPS.map((step, i) => (
                                    <div className="lp-card" key={step.title}>
                                        <span className="lp-card-icon">
                                            <Icon
                                                name={step.icon}
                                                size={22}
                                            />
                                        </span>
                                        <div className="lp-card-title-row">
                                            <span className="lp-step-number">
                                                {String(i + 1).padStart(
                                                    2,
                                                    '0'
                                                )}
                                            </span>
                                            <span className="lp-card-title">
                                                {step.title}
                                            </span>
                                        </div>
                                        <p className="lp-card-body">
                                            {step.body}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* ─── App section ─── */}
                    <section className="lp-section lp-section-surface">
                        <div className="lp-app-grid">
                            <div className="lp-app-image-wrap">
                                <div className="lp-app-frame">
                                    <img
                                        alt="GoPower app — start a charge"
                                        className="lp-app-image"
                                        src={AppScreenImage}
                                    />
                                </div>
                            </div>
                            <div className="lp-app-copy">
                                <h2 className="lp-h2">
                                    One app for{' '}
                                    <span className="green-text">
                                        effortless charging
                                    </span>
                                </h2>
                                <p className="lp-body-text">
                                    No charger to buy, no meters to read. The
                                    GoPower app handles scheduling, payment
                                    and the best rate for you.
                                </p>
                                <ul className="lp-checklist">
                                    {APP_BULLETS.map((bullet) => (
                                        <li key={bullet}>
                                            <span className="lp-check-icon">
                                                <Icon name="check" size={13} />
                                            </span>
                                            {bullet}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* ─── Value props ─── */}
                    <section className="lp-section">
                        <div className="lp-section-inner">
                            <h2 className="lp-h2 lp-h2-center">
                                Built for{' '}
                                <span className="green-text">
                                    multi-family living
                                </span>
                            </h2>
                            <p className="lp-body-text lp-body-center">
                                GoPower is designed for residents of
                                apartments and condos — not single-family
                                garages.
                            </p>
                            <div className="lp-charger-wrap">
                                <img
                                    alt="GoPower PowerPort charger"
                                    className="lp-charger-image"
                                    src={ChargerImage}
                                />
                            </div>
                            <div className="lp-step-grid">
                                {VALUE_PROPS.map((prop) => (
                                    <div className="lp-card" key={prop.title}>
                                        <span className="lp-card-icon">
                                            <Icon name={prop.icon} size={22} />
                                        </span>
                                        <div className="lp-card-title">
                                            {prop.title}
                                        </div>
                                        <p className="lp-card-body">
                                            {prop.body}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>
            )}
        </>
    )
}

export default NewLandingPage
